import React, { useEffect } from 'react';

const CommandPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Command Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Command Pattern - encapsulate requests as objects for undo/redo, queuing, and macro operations. Learn with text editor and remote control examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Command Pattern - encapsulate requests as objects for undo/redo, queuing, and macro operations. Learn with text editor and remote control examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'command pattern, design patterns, behavioral patterns, undo redo, request encapsulation, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'command pattern, design patterns, behavioral patterns, undo redo, request encapsulation, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Command Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Encapsulate requests as objects
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Command pattern encapsulates a request as an object, allowing you to parameterize 
            clients with different requests, queue operations, log requests, and support undo operations. 
            It decouples the object that invokes the operation from the object that performs it.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to issue requests to objects without knowing anything about the operation being 
            requested or the receiver of the request. You want to support undo operations, queuing 
            of requests, or logging of requests.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Text Editor</h2>
          <div style={styles.codeBlock}>
{`// Command interface
interface Command {
    void execute();
    void undo();
}

// Receiver - the object that performs the actual work
class TextEditor {
    private StringBuilder content;
    
    public TextEditor() {
        this.content = new StringBuilder();
    }
    
    public void write(String text) {
        content.append(text);
    }
    
    public void delete(int length) {
        int start = Math.max(0, content.length() - length);
        content.delete(start, content.length());
    }
    
    public String getContent() {
        return content.toString();
    }
    
    public void clear() {
        content.setLength(0);
    }
}

// Concrete commands
class WriteCommand implements Command {
    private TextEditor editor;
    private String text;
    
    public WriteCommand(TextEditor editor, String text) {
        this.editor = editor;
        this.text = text;
    }
    
    @Override
    public void execute() {
        editor.write(text);
    }
    
    @Override
    public void undo() {
        editor.delete(text.length());
    }
}

class DeleteCommand implements Command {
    private TextEditor editor;
    private int length;
    private String deletedText;
    
    public DeleteCommand(TextEditor editor, int length) {
        this.editor = editor;
        this.length = length;
    }
    
    @Override
    public void execute() {
        String content = editor.getContent();
        int start = Math.max(0, content.length() - length);
        deletedText = content.substring(start);
        editor.delete(length);
    }
    
    @Override
    public void undo() {
        if (deletedText != null) {
            editor.write(deletedText);
        }
    }
}

// Invoker - manages commands and undo stack
class EditorInvoker {
    private Stack<Command> history;
    
    public EditorInvoker() {
        this.history = new Stack<>();
    }
    
    public void executeCommand(Command command) {
        command.execute();
        history.push(command);
    }
    
    public void undo() {
        if (!history.isEmpty()) {
            Command command = history.pop();
            command.undo();
        }
    }
}

// Usage
public class CommandPatternDemo {
    public static void main(String[] args) {
        TextEditor editor = new TextEditor();
        EditorInvoker invoker = new EditorInvoker();
        
        // Execute commands
        invoker.executeCommand(new WriteCommand(editor, "Hello "));
        invoker.executeCommand(new WriteCommand(editor, "World!"));
        System.out.println("Content: " + editor.getContent()); // "Hello World!"
        
        invoker.executeCommand(new DeleteCommand(editor, 6));
        System.out.println("After delete: " + editor.getContent()); // "Hello "
        
        // Undo operations
        invoker.undo(); // Undo delete
        System.out.println("After undo: " + editor.getContent()); // "Hello World!"
        
        invoker.undo(); // Undo write "World!"
        System.out.println("After undo: " + editor.getContent()); // "Hello "
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Undo/Redo Support:</strong> Easy to implement undo and redo operations</li>
            <li style={styles.listItem}><strong>Decoupling:</strong> Separates invoker from receiver</li>
            <li style={styles.listItem}><strong>Queuing:</strong> Commands can be queued and executed later</li>
            <li style={styles.listItem}><strong>Logging:</strong> Commands can be logged for audit trails</li>
            <li style={styles.listItem}><strong>Macro Operations:</strong> Multiple commands can be combined</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need to support undo/redo operations</li>
            <li style={styles.listItem}>When you want to queue operations for later execution</li>
            <li style={styles.listItem}>When you need to log operations for auditing</li>
            <li style={styles.listItem}>When you want to decouple the invoker from the receiver</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Command pattern is essential for applications that need undo/redo functionality, 
            operation queuing, or request logging. It provides a clean way to encapsulate operations 
            as objects, making them easy to manage, store, and manipulate.
          </p>
        </section>
      </main>
    </article>
  );
};

export default CommandPattern;
